"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { toast } from "sonner";

export interface EnrollmentSession {
  caseId?: string;
  launchUrl: string;
  carrier?: string;
  product?: string;
  /** Optional lead this case is associated with, for status follow-up. */
  leadId?: string | null;
  /**
   * How the eApp is presented.
   *  - `iframe` (default): embedded modal/floating iframe. Only for
   *    *.techf.com launch URLs (CSP-whitelisted, frameable).
   *  - `popup`: OAuth-style browser popup window. For carrier-site URLs
   *    (Banner Horizon signatures, drop-ticket continues) that block
   *    framing — the popup is a first-party context so their session
   *    cookies work, and X-Frame-Options doesn't apply to popups.
   */
  launchMode?: "iframe" | "popup";
}

/**
 * The iframe surface and the popup surface are INDEPENDENT — an agent can
 * have the Techficient case view embedded in-app (iframe) while the
 * carrier's signature step runs in a popup window at the same time.
 */
interface EnrollmentFloaterState {
  /** Embedded-iframe surface (Techficient pages). */
  open: boolean;
  mode: "modal" | "floating";
  session: EnrollmentSession | null;
  /** Popup-window surface (carrier pages). */
  popupOpen: boolean;
  popupSession: EnrollmentSession | null;
}

interface EnrollmentFloaterApi extends EnrollmentFloaterState {
  /**
   * Open the application — iframe modal by default, floating if
   * `floating: true`. Sessions with `launchMode: "popup"` open a browser
   * popup window instead (must be called synchronously from a user gesture
   * or the popup blocker eats it — returns false when blocked). The two
   * surfaces are independent: launching a popup does NOT close an open
   * iframe, and vice versa.
   */
  launch: (session: EnrollmentSession, opts?: { floating?: boolean }) => boolean;
  /** Convert the iframe surface from modal → floating (keeps the iframe alive). */
  popOut: () => void;
  /** Convert from floating → modal (full focus). */
  maximize: () => void;
  /** Close the IFRAME surface. The popup (if any) stays. */
  close: () => void;
  /** Close the POPUP surface (closes the browser window). Iframe stays. */
  closePopup: () => void;
  /** Re-focus the popup window (it may be behind our tab). */
  focusPopup: () => void;
  /**
   * Deferred popup for async launch flows: call this SYNCHRONOUSLY in the
   * click handler (before any await) to reserve a popup window while the
   * user gesture is still live. Shows a branded "Preparing…" screen. Then
   * either completePendingPopup(session) to navigate it, or
   * cancelPendingPopup() to close it (fetch failed / iframe fallback).
   * Returns false if the popup was blocked.
   */
  openPendingPopup: () => boolean;
  completePendingPopup: (session: EnrollmentSession) => void;
  cancelPendingPopup: () => void;
}

/**
 * Fired on `window` when a popup-mode eApp window closes (agent finished or
 * abandoned the carrier flow). Pages that show application status lists can
 * listen for this to refetch. detail: { caseId?, leadId?, carrier? }
 */
export const EAPP_POPUP_CLOSED_EVENT = "insura:eapp-popup-closed";

/**
 * Native shell: WKWebView returns null for window.open (popup "blocked"), so
 * popup-mode eApps open in the system in-app browser (SFSafariViewController)
 * via @capacitor/browser instead. No handle/focus/poll semantics there — the
 * OS sheet handles it.
 */
const isNativeShell = () =>
  typeof window !== "undefined" &&
  (!!(window as any).Capacitor?.isNativePlatform?.() ||
    window.location.pathname.startsWith("/m"));
const openInNativeBrowser = (url: string) => {
  import("@capacitor/browser")
    .then(({ Browser }) => Browser.open({ url }))
    .catch(() => {});
};

const POPUP_FEATURES = (w: number, h: number) => {
  const width = Math.min(960, w - 80);
  const height = Math.min(760, h - 80);
  const left = Math.max(0, Math.round((w - width) / 2));
  const top = Math.max(0, Math.round((h - height) / 2));
  return `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
};

/**
 * sessionStorage key for the active popup session — lets the popup pill
 * SURVIVE a page refresh. The carrier window itself keeps running (we never
 * close it on unload); after reload we re-acquire its handle by window
 * name on the user's first click (window.open needs a gesture) and resume
 * close-polling.
 */
const POPUP_PERSIST_KEY = "insura-eapp-popup-session";
/**
 * Same idea for the IFRAME surface: the embedded eApp (modal or floating)
 * must survive a page refresh — the launchUrl is a self-authenticating
 * token that stays valid, so re-mounting the iframe resumes the
 * application where the eApp's server-side state left it.
 */
const IFRAME_PERSIST_KEY = "insura-eapp-iframe-session";

const EnrollmentFloaterContext = createContext<EnrollmentFloaterApi | null>(null);

export function EnrollmentFloaterProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<EnrollmentFloaterState>({
    open: false,
    mode: "modal",
    session: null,
    popupOpen: false,
    popupSession: null,
  });
  // Stash the last iframe session so a "close + reopen same session"
  // doesn't lose in-progress eApp state (iframe key stays the same).
  const lastSessionRef = useRef<EnrollmentSession | null>(null);
  // Popup window handle + close-poll timer. Refs, not state.
  const popupRef = useRef<Window | null>(null);
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Window name the popup was opened under — needed to re-acquire the
  // handle after a page refresh.
  const popupNameRef = useRef<string | null>(null);

  const persistPopup = useCallback(
    (session: EnrollmentSession, name: string) => {
      try {
        sessionStorage.setItem(
          POPUP_PERSIST_KEY,
          JSON.stringify({ session, name }),
        );
      } catch {
        /* storage full/blocked — pill just won't survive refresh */
      }
    },
    [],
  );
  const clearPersistedPopup = useCallback(() => {
    try {
      sessionStorage.removeItem(POPUP_PERSIST_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const persistIframe = useCallback(
    (session: EnrollmentSession, mode: "modal" | "floating") => {
      try {
        sessionStorage.setItem(
          IFRAME_PERSIST_KEY,
          JSON.stringify({ session, mode }),
        );
      } catch {
        /* ignore */
      }
    },
    [],
  );
  const clearPersistedIframe = useCallback(() => {
    try {
      sessionStorage.removeItem(IFRAME_PERSIST_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const stopPopupPoll = useCallback(() => {
    if (popupPollRef.current) {
      clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  }, []);

  /** Start polling for the popup being closed by the user. */
  const startPopupPoll = useCallback(
    (session: EnrollmentSession) => {
      stopPopupPoll();
      popupPollRef.current = setInterval(() => {
        if (!popupRef.current || popupRef.current.closed) {
          stopPopupPoll();
          popupRef.current = null;
          popupNameRef.current = null;
          clearPersistedPopup();
          setState((s) => ({ ...s, popupOpen: false, popupSession: null }));
          window.dispatchEvent(
            new CustomEvent(EAPP_POPUP_CLOSED_EVENT, {
              detail: {
                caseId: session.caseId,
                leadId: session.leadId,
                carrier: session.carrier,
              },
            }),
          );
        }
      }, 500);
    },
    [stopPopupPoll, clearPersistedPopup],
  );

  const launch = useCallback(
    (session: EnrollmentSession, opts?: { floating?: boolean }): boolean => {
      if (session.launchMode === "popup") {
        // Native shell: window.open is dead in WKWebView — popup-mode eApps
        // go to the OS in-app browser sheet. Iframe-mode stays in the
        // floating window (boss's call: keep the floater on iOS).
        if (isNativeShell()) {
          openInNativeBrowser(session.launchUrl);
          lastSessionRef.current = session;
          return true;
        }
        // Re-launch while the same CASE's popup is open → just focus it.
        // Match by caseId, NOT launchUrl: Resume always mints a fresh URL,
        // and navigating the existing window to it restarts the carrier's
        // session from page one (the Banner "resume took me to the
        // beginning" bug). launchUrl equality kept as a fallback for
        // case-less sessions.
        if (
          popupRef.current &&
          !popupRef.current.closed &&
          ((session.caseId &&
            state.popupSession?.caseId === session.caseId) ||
            state.popupSession?.launchUrl === session.launchUrl)
        ) {
          popupRef.current.focus();
          return true;
        }
        // MUST run synchronously inside the user's click for the popup
        // blocker to allow it. `noopener` deliberately NOT set — we need
        // the handle for focus + closed-polling.
        const popupName = `insura-eapp-${session.caseId ?? "case"}`;
        const popup = window.open(
          session.launchUrl,
          popupName,
          POPUP_FEATURES(window.screen.availWidth, window.screen.availHeight),
        );
        if (!popup) return false;
        popupRef.current = popup;
        popupNameRef.current = popupName;
        persistPopup(session, popupName);
        // Popup surface only — the iframe surface is untouched so the
        // Techficient case view can stay visible behind the popup.
        setState((s) => ({ ...s, popupOpen: true, popupSession: session }));
        startPopupPoll(session);
        return true;
      }

      lastSessionRef.current = session;
      // Modal renders INLINE on /quote-enroll only — launching from any
      // other surface (lead details, InsuraBot on another page) must come
      // up floating or nothing visible appears. Mirrors the restore logic.
      const onQuotePage =
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/quote-enroll") ||
        window.location.pathname.startsWith("/m/quote-enroll");
      const mode =
        opts?.floating || !onQuotePage
          ? ("floating" as const)
          : ("modal" as const);
      persistIframe(session, mode);
      setState((s) => ({
        ...s,
        open: true,
        mode,
        session,
      }));
      return true;
    },
    [startPopupPoll, state.popupSession?.launchUrl, persistIframe],
  );

  const popOut = useCallback(() => {
    setState((s) => {
      if (!s.open) return s;
      if (s.session) persistIframe(s.session, "floating");
      return { ...s, mode: "floating" };
    });
  }, [persistIframe]);

  const maximize = useCallback(() => {
    setState((s) => {
      if (!s.open) return s;
      if (s.session) persistIframe(s.session, "modal");
      return { ...s, mode: "modal" };
    });
  }, [persistIframe]);

  const close = useCallback(() => {
    clearPersistedIframe();
    setState((s) => ({ ...s, open: false, mode: "modal", session: null }));
  }, [clearPersistedIframe]);

  const closePopup = useCallback(() => {
    stopPopupPoll();
    if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    popupRef.current = null;
    popupNameRef.current = null;
    clearPersistedPopup();
    setState((s) => ({ ...s, popupOpen: false, popupSession: null }));
  }, [stopPopupPoll, clearPersistedPopup]);

  const focusPopup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) popupRef.current.focus();
  }, []);

  // Guard accidental refresh/close while an application is open in the
  // embedded iframe — a reload drops the wizard back to its last committed
  // section (in-memory position is lost). The browser shows its native
  // "Leave site?" prompt; deliberate leaves still work. The popup surface
  // needs no guard (it's its own window and survives our reloads).
  useEffect(() => {
    if (!state.open || !state.session) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [state.open, state.session]);

  // ── Deferred popup (async launch flows) ─────────────────────────────
  const openPendingPopup = useCallback((): boolean => {
    // Native shell: Browser.open works outside the click gesture — nothing
    // to reserve, never "blocked".
    if (isNativeShell()) return true;
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return true;
    }
    const popup = window.open(
      "about:blank",
      "insura-eapp-pending",
      POPUP_FEATURES(window.screen.availWidth, window.screen.availHeight),
    );
    if (!popup) return false;
    try {
      // Holding screen while the submit round-trips. Static text via DOM
      // methods (no document.write / no injected markup).
      const doc = popup.document;
      doc.body.style.cssText =
        "display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui,sans-serif;color:#334155;background:#f8fafc";
      const wrap = doc.createElement("div");
      wrap.style.textAlign = "center";
      const title = doc.createElement("div");
      title.style.cssText = "font-size:15px;font-weight:600";
      title.textContent = "Preparing your application…";
      const sub = doc.createElement("div");
      sub.style.cssText = "font-size:12px;color:#94a3b8;margin-top:6px";
      sub.textContent =
        "Submitting to the carrier — this window will load the signature step.";
      wrap.appendChild(title);
      wrap.appendChild(sub);
      doc.body.appendChild(wrap);
    } catch {
      /* cosmetic only */
    }
    popupRef.current = popup;
    return true;
  }, []);

  const completePendingPopup = useCallback(
    (session: EnrollmentSession) => {
      if (isNativeShell()) {
        openInNativeBrowser(session.launchUrl);
        return;
      }
      const popup = popupRef.current;
      if (!popup || popup.closed) {
        // User closed the holding window before the backend came back —
        // respect that as a cancel.
        popupRef.current = null;
        return;
      }
      popup.location.href = session.launchUrl;
      popupNameRef.current = "insura-eapp-pending";
      persistPopup({ ...session, launchMode: "popup" }, "insura-eapp-pending");
      setState((s) => ({
        ...s,
        popupOpen: true,
        popupSession: { ...session, launchMode: "popup" },
      }));
      startPopupPoll(session);
    },
    [startPopupPoll, persistPopup],
  );

  const cancelPendingPopup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    popupRef.current = null;
  }, []);

  // Leave-page hygiene: stop polling on unmount (popup itself stays open —
  // killing the agent's half-finished carrier application on a CRM refresh
  // would be hostile).
  useEffect(() => stopPopupPoll, [stopPopupPoll]);

  // ── Survive page refresh ────────────────────────────────────────────
  // The carrier window keeps running across our reloads, but the handle is
  // lost. Restore the pill from sessionStorage immediately, then re-acquire
  // the window on the user's FIRST CLICK anywhere (window.open needs a
  // gesture): opening `window.open("", sameName)` returns the EXISTING
  // window without navigating it. If that yields a fresh blank window
  // instead, the agent closed the popup while we were away — clean up.
  useEffect(() => {
    // Restore the IFRAME surface first — no gesture needed; the launch
    // token stays valid and the eApp resumes from its server-side state.
    try {
      const rawIframe = sessionStorage.getItem(IFRAME_PERSIST_KEY);
      if (rawIframe) {
        const savedIframe = JSON.parse(rawIframe) as {
          session: EnrollmentSession;
          mode: "modal" | "floating";
        };
        if (savedIframe?.session?.launchUrl) {
          lastSessionRef.current = savedIframe.session;
          // Modal renders INLINE on /quote-enroll only — if the reload
          // landed anywhere else, restore as the floating window so the
          // application is visible instead of silently open-but-hidden.
          const onQuoteEnroll =
            typeof window !== "undefined" &&
            window.location.pathname.startsWith("/quote-enroll") ||
        window.location.pathname.startsWith("/m/quote-enroll");
          const mode =
            savedIframe.mode === "floating" || !onQuoteEnroll
              ? ("floating" as const)
              : ("modal" as const);
          setState((s) => ({
            ...s,
            open: true,
            mode,
            session: savedIframe.session,
          }));
          // The stored URL is the stale initiate-time token — replaying it
          // makes the eApp re-enter at an EARLIER point than a proper
          // resume ("I was at 60%, refresh sent me to 20%"). Swap in a
          // fresh resume URL as soon as the backend hands us one; the
          // stored URL stays as the fallback if the fetch fails.
          const caseId = savedIframe.session.caseId;
          if (caseId) {
            void import("@/app/_module/services/techficientService")
              .then((m) => m.techficientService.resumeEapp(caseId))
              .then((fresh: any) => {
                if (!fresh?.launchUrl || fresh.launchMode === "popup") return;
                setState((s) => {
                  if (!s.open || s.session?.caseId !== caseId) return s;
                  const upd = { ...s.session!, launchUrl: fresh.launchUrl };
                  lastSessionRef.current = upd;
                  persistIframe(upd, s.mode);
                  return { ...s, session: upd };
                });
              })
              .catch(() => {
                /* stale token remains the fallback */
              });
          }
        }
      }
    } catch {
      /* corrupted entry — drop it */
      try {
        sessionStorage.removeItem(IFRAME_PERSIST_KEY);
      } catch {
        /* ignore */
      }
    }

    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(POPUP_PERSIST_KEY);
    } catch {
      return;
    }
    if (!raw) return;
    let saved: { session: EnrollmentSession; name: string };
    try {
      saved = JSON.parse(raw);
    } catch {
      clearPersistedPopup();
      return;
    }
    if (!saved?.session?.launchUrl || !saved?.name) return;

    setState((s) => ({
      ...s,
      popupOpen: true,
      popupSession: saved.session,
    }));

    const reacquire = () => {
      // Guard: a new popup may have been opened since restore.
      if (popupRef.current && !popupRef.current.closed) return cleanup();
      const w = window.open("", saved.name);
      if (!w) return; // blocked — try again on the next click
      let isFreshBlank = false;
      try {
        // Same-origin read succeeds only for a NEW about:blank window we
        // just created; the real carrier window is cross-origin and throws.
        isFreshBlank = w.location.href === "about:blank";
      } catch {
        isFreshBlank = false;
      }
      if (isFreshBlank) {
        w.close();
        clearPersistedPopup();
        setState((s) => ({ ...s, popupOpen: false, popupSession: null }));
        window.dispatchEvent(
          new CustomEvent(EAPP_POPUP_CLOSED_EVENT, {
            detail: {
              caseId: saved.session.caseId,
              leadId: saved.session.leadId,
              carrier: saved.session.carrier,
            },
          }),
        );
      } else {
        popupRef.current = w;
        popupNameRef.current = saved.name;
        startPopupPoll(saved.session);
      }
      cleanup();
    };
    const cleanup = () => {
      document.removeEventListener("click", reacquire, true);
    };
    document.addEventListener("click", reacquire, true);
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Techficient postMessage bridge ──────────────────────────────────
  // Protocol agreed with Techficient: instead of window.open (which we
  // cannot intercept cross-origin and which escapes as a browser tab),
  // their embedded pages emit:
  //   window.parent.postMessage({ type: 'open_url', url: '<https url>' }, '*')
  // We verify the SENDER ORIGIN is techf.com, parse the URL, and route:
  // techf.com hosts → embedded iframe; anything else (carrier signature
  // pages) → popup window. postMessage carries no user-activation, so a
  // blocked popup degrades to a one-click "Open" toast — never a tab.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      // Sender must be a techf origin — ignore everything else.
      let senderHost = "";
      try {
        senderHost = new URL(e.origin).hostname.toLowerCase();
      } catch {
        return;
      }
      if (senderHost !== "techf.com" && !senderHost.endsWith(".techf.com"))
        return;
      const data = e.data;
      if (!data || data.type !== "open_url" || typeof data.url !== "string")
        return;
      let parsed: URL;
      try {
        parsed = new URL(data.url);
      } catch {
        return;
      }
      if (parsed.protocol !== "https:") return;
      const host = parsed.hostname.toLowerCase().replace(/\.$/, "");
      const isTechf = host === "techf.com" || host.endsWith(".techf.com");

      if (isTechf) {
        // In-app embed — no gesture required.
        lastSessionRef.current = { launchUrl: parsed.toString() };
        setState((s) => {
          const mode = s.mode === "floating" ? ("floating" as const) : ("modal" as const);
          persistIframe({ launchUrl: parsed.toString() }, mode);
          return {
            ...s,
            open: true,
            mode,
            session: { launchUrl: parsed.toString() },
          };
        });
        return;
      }

      // Carrier page → popup. May be blocked (no user activation crosses
      // postMessage); fall back to a one-click opener toast.
      const session: EnrollmentSession = {
        launchUrl: parsed.toString(),
        launchMode: "popup",
      };
      const popup = window.open(
        session.launchUrl,
        "insura-eapp-bridge",
        POPUP_FEATURES(window.screen.availWidth, window.screen.availHeight),
      );
      if (popup) {
        popupRef.current = popup;
        popupNameRef.current = "insura-eapp-bridge";
        persistPopup(session, "insura-eapp-bridge");
        setState((s) => ({ ...s, popupOpen: true, popupSession: session }));
        startPopupPoll(session);
      } else {
        toast("The next step opens in its own window", {
          description: host,
          action: {
            label: "Open",
            onClick: () => {
              const p = window.open(
                session.launchUrl,
                "insura-eapp-bridge",
                POPUP_FEATURES(
                  window.screen.availWidth,
                  window.screen.availHeight,
                ),
              );
              if (p) {
                popupRef.current = p;
                popupNameRef.current = "insura-eapp-bridge";
                persistPopup(session, "insura-eapp-bridge");
                setState((s) => ({
                  ...s,
                  popupOpen: true,
                  popupSession: session,
                }));
                startPopupPoll(session);
              }
            },
          },
          duration: 20000,
        });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [startPopupPoll, persistPopup]);

  const value = useMemo<EnrollmentFloaterApi>(
    () => ({
      ...state,
      launch,
      popOut,
      maximize,
      close,
      closePopup,
      focusPopup,
      openPendingPopup,
      completePendingPopup,
      cancelPendingPopup,
    }),
    [
      state,
      launch,
      popOut,
      maximize,
      close,
      closePopup,
      focusPopup,
      openPendingPopup,
      completePendingPopup,
      cancelPendingPopup,
    ],
  );

  return (
    <EnrollmentFloaterContext.Provider value={value}>
      {children}
    </EnrollmentFloaterContext.Provider>
  );
}

export function useEnrollmentFloater(): EnrollmentFloaterApi {
  const ctx = useContext(EnrollmentFloaterContext);
  if (!ctx) {
    // Graceful fallback for components mounted outside the provider.
    return {
      open: false,
      mode: "modal",
      session: null,
      popupOpen: false,
      popupSession: null,
      launch: () => false,
      popOut: () => undefined,
      maximize: () => undefined,
      close: () => undefined,
      closePopup: () => undefined,
      focusPopup: () => undefined,
      openPendingPopup: () => false,
      completePendingPopup: () => undefined,
      cancelPendingPopup: () => undefined,
    };
  }
  return ctx;
}
